/**
 * SECURITY GATE - Phase 7: Security & Recovery Suite
 * 
 * This component handles:
 * 1. Lock Screen (if wallet exists) - PIN entry required
 * 2. Start Screen (if no wallet) - Create or Import options
 * 3. Import Wallet Modal - Private key input + validation
 * 4. PIN Setup Flow - Required after create/import
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Key, Eye, EyeOff, Shield, Wallet, AlertTriangle, 
  RefreshCw, Lock, Unlock, ChevronLeft, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Keypair } from '@solana/web3.js';
import bs58 from 'bs58';

// Validate Solana private key
const validateSolanaPrivateKey = (keyBase58) => {
  try {
    const secretKeyBytes = bs58.decode(keyBase58);
    if (secretKeyBytes.length !== 64) {
      return { valid: false, error: 'Invalid key length' };
    }
    const keypair = Keypair.fromSecretKey(secretKeyBytes);
    const publicKey = keypair.publicKey.toBase58();
    return { 
      valid: true, 
      publicKey,
      secretKey: keyBase58,
      shortId: `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`
    };
  } catch (e) {
    return { valid: false, error: 'Invalid private key format' };
  }
};

// PIN Keypad Component
const PinKeypad = ({ onPinComplete, title, subtitle, isSetup = false, onBack }) => {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isConfirmStep, setIsConfirmStep] = useState(false);
  const [error, setError] = useState('');

  const handleKeyPress = (key) => {
    const currentPin = isSetup && isConfirmStep ? confirmPin : pin;
    const setCurrentPin = isSetup && isConfirmStep ? setConfirmPin : setPin;
    
    if (key === 'clear') {
      setCurrentPin('');
      setError('');
      return;
    }
    
    if (key === 'back') {
      setCurrentPin(currentPin.slice(0, -1));
      setError('');
      return;
    }
    
    if (currentPin.length < 4) {
      const newPin = currentPin + key;
      setCurrentPin(newPin);
      
      if (newPin.length === 4) {
        if (isSetup) {
          if (!isConfirmStep) {
            // First entry - go to confirm
            setIsConfirmStep(true);
            setTimeout(() => setPin(newPin), 100);
          } else {
            // Confirm step - check match
            if (newPin === pin) {
              onPinComplete(newPin);
            } else {
              setError('PINs do not match. Try again.');
              setConfirmPin('');
              setIsConfirmStep(false);
              setPin('');
            }
          }
        } else {
          // Login mode - just submit
          onPinComplete(newPin);
        }
      }
    }
  };

  const displayPin = isSetup && isConfirmStep ? confirmPin : pin;

  return (
    <div className="flex flex-col items-center">
      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-4 left-4 text-white/60 hover:text-white flex items-center gap-1"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
      )}

      {/* Title */}
      <h2 className="font-anton text-2xl text-white mb-2 text-center">
        {isSetup && isConfirmStep ? 'CONFIRM YOUR PIN' : title}
      </h2>
      <p className="text-white/60 text-sm mb-8 text-center">
        {isSetup && isConfirmStep ? 'Enter the same PIN again' : subtitle}
      </p>

      {/* PIN Display */}
      <div className="flex gap-4 mb-8">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-bold transition-all ${
              displayPin.length > i 
                ? 'bg-green-500/30 border-green-500 text-green-400' 
                : 'bg-white/5 border-white/20 text-white/20'
            }`}
          >
            {displayPin.length > i ? '●' : '○'}
          </div>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-red-400 text-sm mb-4 flex items-center gap-2"
        >
          <XCircle className="w-4 h-4" />
          {error}
        </motion.div>
      )}

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'clear', 0, 'back'].map((key) => (
          <button
            key={key}
            onClick={() => handleKeyPress(key.toString())}
            className={`w-16 h-16 rounded-xl font-bold text-xl transition-all ${
              key === 'clear' 
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm' 
                : key === 'back'
                ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 text-sm'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {key === 'clear' ? 'CLR' : key === 'back' ? '←' : key}
          </button>
        ))}
      </div>
    </div>
  );
};

// Import Wallet Modal
const ImportWalletModal = ({ onImport, onClose }) => {
  const [privateKey, setPrivateKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [validatedWallet, setValidatedWallet] = useState(null);

  const handleValidate = () => {
    setIsValidating(true);
    setError('');
    
    setTimeout(() => {
      const result = validateSolanaPrivateKey(privateKey.trim());
      
      if (result.valid) {
        setValidatedWallet(result);
        toast.success('Wallet validated!', {
          description: `Address: ${result.shortId}`
        });
      } else {
        setError(result.error);
        setValidatedWallet(null);
      }
      setIsValidating(false);
    }, 500);
  };

  const handleImport = () => {
    if (validatedWallet) {
      onImport(validatedWallet);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-[#0a0a0a] border-2 border-yellow-500/50 rounded-2xl p-6 w-full max-w-md"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
            <Key className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-anton text-xl text-white">IMPORT WALLET</h2>
            <p className="text-white/60 text-sm">Recover your existing account</p>
          </div>
        </div>

        {/* Private Key Input */}
        <div className="mb-4">
          <label className="text-white/60 text-sm mb-2 block">Private Key (Base58)</label>
          <div className="relative">
            <textarea
              value={privateKey}
              onChange={(e) => {
                setPrivateKey(e.target.value);
                setValidatedWallet(null);
                setError('');
              }}
              placeholder="Enter your Solana private key..."
              rows={3}
              className={`w-full bg-black/50 border rounded-xl p-3 text-white placeholder:text-white/30 focus:outline-none font-mono text-sm resize-none ${
                error ? 'border-red-500' : validatedWallet ? 'border-green-500' : 'border-white/20 focus:border-yellow-500'
              }`}
              style={{ 
                WebkitTextSecurity: showKey ? 'none' : 'disc',
                fontFamily: showKey ? 'monospace' : 'inherit'
              }}
              data-testid="import-private-key-input"
            />
            <button
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-3 text-white/40 hover:text-white/60"
            >
              {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          
          {/* Error */}
          {error && (
            <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              {error}
            </p>
          )}
          
          {/* Validated Address Preview */}
          {validatedWallet && (
            <div className="mt-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Wallet Verified!
              </div>
              <p className="text-green-200/70 text-xs mt-1 font-mono">
                {validatedWallet.publicKey}
              </p>
            </div>
          )}
        </div>

        {/* Warning */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
            <p className="text-yellow-200/80 text-xs">
              Your private key is stored locally and never sent to any server. 
              Make sure you're on the correct website.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={onClose}
            className="flex-1 bg-white/10 border border-white/20 text-white hover:bg-white/20"
          >
            Cancel
          </Button>
          
          {!validatedWallet ? (
            <Button
              onClick={handleValidate}
              disabled={!privateKey.trim() || isValidating}
              className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold disabled:opacity-50"
            >
              {isValidating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                'Validate Key'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleImport}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Import & Continue
            </Button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Forgot PIN Modal
const ForgotPinModal = ({ onReset, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-[#0a0a0a] border-2 border-red-500/50 rounded-2xl p-6 w-full max-w-md"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-anton text-2xl text-white mb-2">RESET WALLET?</h2>
          <p className="text-white/60 text-sm">
            This will remove your current wallet from this device.
          </p>
        </div>

        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
          <p className="text-red-200/80 text-sm text-center">
            ⚠️ Make sure you have your <strong>Private Key</strong> backed up!<br/>
            Without it, you cannot recover your wallet and funds.
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onClose}
            className="flex-1 bg-white/10 border border-white/20 text-white hover:bg-white/20"
          >
            Cancel
          </Button>
          <Button
            onClick={onReset}
            className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold"
          >
            Reset & Re-import
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Security Gate Component
const SecurityGate = ({ children, onUnlock }) => {
  const [screenState, setScreenState] = useState('loading'); // loading, locked, start, import, pin-setup
  const [existingWallet, setExistingWallet] = useState(null);
  const [pendingWallet, setPendingWallet] = useState(null);
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [pinError, setPinError] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Check for existing wallet on mount
  useEffect(() => {
    const checkWallet = () => {
      const storedPin = localStorage.getItem('user_pin');
      const storedWallet = localStorage.getItem('solana_wallet');
      
      if (storedPin && storedWallet) {
        // Wallet exists with PIN - show lock screen
        try {
          const wallet = JSON.parse(storedWallet);
          setExistingWallet(wallet);
          setScreenState('locked');
        } catch (e) {
          // Corrupted data - reset
          setScreenState('start');
        }
      } else if (storedWallet && !storedPin) {
        // Wallet exists but no PIN - need to set one (migration)
        try {
          const wallet = JSON.parse(storedWallet);
          setPendingWallet(wallet);
          setScreenState('pin-setup');
        } catch (e) {
          setScreenState('start');
        }
      } else {
        // No wallet - show start screen
        setScreenState('start');
      }
    };

    // Small delay for smoother UX
    setTimeout(checkWallet, 300);
  }, []);

  // Handle PIN verification for locked screen
  const handlePinVerify = (enteredPin) => {
    const storedPin = localStorage.getItem('user_pin');
    
    if (enteredPin === storedPin) {
      setIsUnlocked(true);
      toast.success('Welcome back!');
      onUnlock();
    } else {
      setPinError('Incorrect PIN. Try again.');
      toast.error('Incorrect PIN');
    }
  };

  // Handle creating new wallet
  const handleCreateWallet = () => {
    // Generate new Solana wallet
    const keypair = Keypair.generate();
    const publicKey = keypair.publicKey.toBase58();
    const secretKey = bs58.encode(keypair.secretKey);
    
    const newWallet = {
      publicKey,
      secretKey,
      shortId: `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`,
      createdAt: Date.now()
    };
    
    setPendingWallet(newWallet);
    setScreenState('pin-setup');
    toast.success('Wallet generated!');
  };

  // Handle importing wallet
  const handleImportWallet = (validatedWallet) => {
    const newWallet = {
      publicKey: validatedWallet.publicKey,
      secretKey: validatedWallet.secretKey,
      shortId: validatedWallet.shortId,
      createdAt: Date.now()
    };
    
    setPendingWallet(newWallet);
    setScreenState('pin-setup');
    toast.success('Wallet imported!');
  };

  // Handle PIN setup complete
  const handlePinSetup = (pin) => {
    if (!pendingWallet) return;
    
    // Save wallet and PIN
    localStorage.setItem('solana_wallet', JSON.stringify(pendingWallet));
    localStorage.setItem('player_id', pendingWallet.publicKey);
    localStorage.setItem('user_pin', pin);
    
    setIsUnlocked(true);
    toast.success('Account secured!', {
      description: 'Your wallet is now protected with PIN'
    });
    onUnlock();
  };

  // Handle wallet reset (forgot PIN)
  const handleWalletReset = () => {
    localStorage.removeItem('solana_wallet');
    localStorage.removeItem('player_id');
    localStorage.removeItem('user_pin');
    localStorage.removeItem('solana_priv_key');
    
    setExistingWallet(null);
    setPendingWallet(null);
    setShowForgotPin(false);
    setScreenState('start');
    toast.success('Wallet removed. You can now import your key.');
  };

  // If unlocked, render children
  if (isUnlocked) {
    return children;
  }

  // Loading state
  if (screenState === 'loading') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center animate-pulse">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-anton text-xl text-white">SLAP BATTLES</h2>
          <p className="text-white/40 text-sm mt-2">Loading...</p>
        </motion.div>
      </div>
    );
  }

  // Lock Screen (existing wallet with PIN)
  if (screenState === 'locked') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center relative overflow-hidden">
        {/* Background Grid */}
        <div 
          className="fixed inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(#00FF9915 1px, transparent 1px), linear-gradient(90deg, #00FF9915 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center px-4"
        >
          {/* Lock Icon */}
          <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <Lock className="w-12 h-12 text-white" />
          </div>

          {/* Welcome Back */}
          <h1 className="font-anton text-3xl text-white mb-2">WELCOME BACK</h1>
          <p className="text-white/60 text-sm mb-2">
            {existingWallet?.shortId || 'Your Wallet'}
          </p>

          {/* PIN Error */}
          {pinError && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-sm mb-4"
              onAnimationComplete={() => setTimeout(() => setPinError(''), 2000)}
            >
              {pinError}
            </motion.p>
          )}

          {/* PIN Keypad */}
          <div className="mt-8">
            <PinKeypad
              title="ENTER YOUR PIN"
              subtitle="Enter your 4-digit PIN to unlock"
              onPinComplete={handlePinVerify}
            />
          </div>

          {/* Forgot PIN Link */}
          <button
            onClick={() => setShowForgotPin(true)}
            className="mt-8 text-yellow-400/80 hover:text-yellow-400 text-sm underline"
          >
            Forgot PIN?
          </button>
        </motion.div>

        {/* Forgot PIN Modal */}
        <AnimatePresence>
          {showForgotPin && (
            <ForgotPinModal
              onReset={handleWalletReset}
              onClose={() => setShowForgotPin(false)}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Start Screen (no wallet)
  if (screenState === 'start') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center relative overflow-hidden">
        {/* Background Grid */}
        <div 
          className="fixed inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(#FF00FF15 1px, transparent 1px), linear-gradient(90deg, #FF00FF15 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center px-4 max-w-md mx-auto"
        >
          {/* Logo */}
          <div className="w-28 h-28 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center shadow-2xl shadow-purple-500/30">
            <Zap className="w-14 h-14 text-white" />
          </div>

          <h1 
            className="font-anton text-5xl uppercase tracking-tight mb-2"
            style={{
              background: 'linear-gradient(180deg, #FF00FF 0%, #00FFFF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            SLAP BATTLES
          </h1>
          <p className="text-white/60 mb-10">
            Crypto Arcade • Play to Earn
          </p>

          {/* Create New Account Button */}
          <Button
            onClick={handleCreateWallet}
            className="w-full py-6 mb-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white font-bold text-lg rounded-2xl"
            data-testid="create-new-account-btn"
          >
            <Zap className="w-6 h-6 mr-3" />
            CREATE NEW ACCOUNT
          </Button>

          {/* Import Wallet Button */}
          <Button
            onClick={() => setScreenState('import')}
            className="w-full py-5 bg-white/10 border-2 border-yellow-500/50 hover:bg-yellow-500/10 text-yellow-400 font-bold text-base rounded-2xl"
            data-testid="import-wallet-btn"
          >
            <Key className="w-5 h-5 mr-3" />
            I ALREADY HAVE A WALLET
          </Button>

          {/* Footer */}
          <p className="text-white/30 text-xs mt-10">
            Your wallet is stored locally on this device
          </p>
        </motion.div>
      </div>
    );
  }

  // Import Wallet Screen
  if (screenState === 'import') {
    return (
      <ImportWalletModal
        onImport={handleImportWallet}
        onClose={() => setScreenState('start')}
      />
    );
  }

  // PIN Setup Screen
  if (screenState === 'pin-setup') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center relative overflow-hidden">
        {/* Background Grid */}
        <div 
          className="fixed inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(#00FF9915 1px, transparent 1px), linear-gradient(90deg, #00FF9915 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center px-4"
        >
          {/* Shield Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Shield className="w-10 h-10 text-white" />
          </div>

          {/* Wallet Address Preview */}
          {pendingWallet && (
            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 mb-6 inline-block">
              <p className="text-white/40 text-xs">Your Wallet</p>
              <p className="text-green-400 font-mono text-sm">{pendingWallet.shortId}</p>
            </div>
          )}

          {/* PIN Keypad */}
          <PinKeypad
            title="PROTECT YOUR ACCOUNT"
            subtitle="Create a 4-digit PIN"
            isSetup={true}
            onPinComplete={handlePinSetup}
            onBack={() => {
              setPendingWallet(null);
              setScreenState('start');
            }}
          />

          {/* Info */}
          <p className="text-white/30 text-xs mt-8 max-w-xs mx-auto">
            This PIN will be required every time you open the app
          </p>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default SecurityGate;
